(() => {
  const CART_FETCH_OPTS = { credentials: 'same-origin', headers: { 'Accept': 'application/json' } };

  function formatAmount(cents, placeholder) {
    const noDecimals = placeholder.indexOf('no_decimals') !== -1;
    const commaSep = placeholder.indexOf('comma_separator') !== -1;
    const spaceSep = placeholder.indexOf('space_separator') !== -1;
    const apostropheSep = placeholder.indexOf('apostrophe_separator') !== -1;

    let thousands = ',';
    let decimal = '.';
    if (commaSep) { thousands = '.'; decimal = ','; }
    else if (spaceSep) { thousands = ' '; decimal = '.'; }
    else if (apostropheSep) { thousands = "'"; decimal = '.'; }

    const amount = cents / 100;
    const raw = noDecimals ? Math.round(amount).toString() : amount.toFixed(2);
    const parts = raw.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
  }

  function money(cents, currencyFormat) {
    const fmt = currencyFormat || '${{amount}}';
    return fmt.replace(/\{\{\s*(amount[^}\s]*)\s*\}\}/g, (_, placeholder) => formatAmount(cents, placeholder));
  }

  class LaliCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.cart = null;
      this.isOpen = false;
      this.pageMode = this.dataset.pageMode === 'true';
      this.threshold = parseInt(this.dataset.freeShippingThreshold || '0', 10);
      this.currencyFormat = this.dataset.currencyFormat || '${{amount}}';
      this.boundOnDocClick = this.onDocClick.bind(this);
      this.boundOnEsc = this.onEsc.bind(this);
    }

    connectedCallback() {
      this.overlay = this.querySelector('[data-cart-overlay]');
      this.panel = this.querySelector('[data-cart-panel]');
      this.itemsEl = this.querySelector('[data-cart-items]');
      this.emptyEl = this.querySelector('[data-cart-empty]');
      this.footerEl = this.querySelector('[data-cart-footer]');
      this.subtotalEl = this.querySelector('[data-cart-subtotal]');
      this.payBtn = this.querySelector('[data-cart-checkout]');
      this.upsellEl = this.querySelector('[data-cart-upsells]');
      this.bannerEl = this.querySelector('[data-cart-shipping-banner]');

      this.querySelector('[data-cart-close]')?.addEventListener('click', () => this.close());
      this.overlay?.addEventListener('click', () => this.close());
      this.addEventListener('click', this.onClick.bind(this));

      if (!this.pageMode) {
        this.attachCartTriggers();
        this.attachATCInterceptor();
        this.refresh();
      }

      document.addEventListener('lali:cart:open', () => this.open());
      document.addEventListener('lali:cart:refresh', () => this.refresh());
      document.addEventListener('instant:cart-updated', () => this.refresh());
      document.addEventListener('instant:add-to-cart', () => { this.refresh(); this.open(); });
    }

    attachCartTriggers() {
      const isCartTrigger = (el) => {
        if (!el || this.contains(el)) return false;
        if (el.hasAttribute('data-cart-no-drawer')) return false;
        if (el.matches('[data-cart-trigger], #cart-icon-bubble, cart-drawer-component, [data-action="open-cart"], [aria-controls="cart-drawer"]')) return true;
        if (el.closest('#cart-icon-bubble, cart-drawer-component, [data-cart-trigger]')) return true;
        const href = el.getAttribute && el.getAttribute('href');
        if (href) {
          if (/\/cart\/(add|change|update|clear)/.test(href)) return false;
          // Shopify cart permalinks /cart/VARIANT_ID:QTY[,VARIANT_ID:QTY,...] add items and redirect.
          // Don't intercept those — let them navigate so Shopify processes the ATC.
          if (/\/cart\/\d+:\d+/.test(href)) return false;
          if (/(?:^|\/)cart(?:[?#]|$)/.test(href)) return true;
        }
        return false;
      };

      document.addEventListener('click', (e) => {
        const candidate = e.target.closest('a, button, summary, [role="button"], #cart-icon-bubble, cart-drawer-component, [data-cart-trigger]');
        if (!candidate || !isCartTrigger(candidate)) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        this.open();
      }, true);

      // Intercept Shopify cart permalinks (/cart/VARIANT_ID:QTY?storefront=true) so the user
      // sees the drawer open with the freshly-added product, instead of a full page redirect.
      document.addEventListener('click', async (e) => {
        const link = e.target.closest('a[href*="/cart/"]');
        if (!link) return;
        if (this.contains(link)) return;
        const href = link.getAttribute('href') || '';
        const m = href.match(/\/cart\/((?:\d+:\d+,?)+)/);
        if (!m) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        try {
          const items = m[1].split(',').map((pair) => {
            const [id, qty] = pair.split(':');
            return { id: Number(id), quantity: Number(qty) || 1 };
          });
          for (const it of items) {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify(it),
            });
            if (!res.ok) throw new Error(await res.text());
            await res.json();
          }
          document.dispatchEvent(new CustomEvent('lali:cart:added'));
          await this.refresh();
          this.open();
        } catch (err) {
          console.error('Cart permalink ATC failed', err);
          // Fallback: let the browser follow the link so Shopify processes it.
          window.location.href = href;
        }
      }, true);
    }

    attachATCInterceptor() {
      document.addEventListener('submit', async (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.action && /\/cart\/add/.test(form.action)) {
          e.preventDefault();
          const formData = new FormData(form);
          try {
            const res = await fetch('/cart/add.js', { method: 'POST', body: formData, headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
            if (!res.ok) throw new Error(await res.text());
            await res.json();
            document.dispatchEvent(new CustomEvent('lali:cart:added'));
            await this.refresh();
            this.open();
          } catch (err) {
            console.error('Add to cart failed', err);
            form.submit();
          }
        }
      }, true);
    }

    onDocClick(e) {
      if (!this.contains(e.target) && this.isOpen) this.close();
    }

    onEsc(e) { if (e.key === 'Escape') this.close(); }

    onClick(e) {
      const qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn) {
        e.preventDefault();
        const line = parseInt(qtyBtn.dataset.line, 10);
        const delta = parseInt(qtyBtn.dataset.cartQty, 10);
        this.changeQty(line, delta);
        return;
      }
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        const line = parseInt(removeBtn.dataset.line, 10);
        this.removeLine(line);
        return;
      }
      const addUpsell = e.target.closest('[data-upsell-add]');
      if (addUpsell) {
        e.preventDefault();
        const variantId = addUpsell.dataset.variantId;
        if (variantId) this.addVariant(variantId);
      }
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.boundOnEsc);
      this.panel?.focus?.();
      this.publishCartViewed();
    }

    publishCartViewed() {
      if (this.pageMode || !this.cart) return;
      const total = (this.cart.total_price || 0) / 100;
      const items = (this.cart.items || []).map(i => ({
        item_id: String(i.product_id),
        item_variant_id: String(i.variant_id),
        item_name: i.product_title,
        item_variant: i.variant_title || '',
        price: (i.final_price || 0) / 100,
        quantity: i.quantity
      }));
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'view_cart',
          ecommerce: { currency: this.cart.currency || 'COP', value: total, items }
        });
      } catch (e) {}
      try {
        if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
          window.Shopify.analytics.publish('cart_viewed', { cart: this.cart });
        }
      } catch (e) {}
    }

    close() {
      this.isOpen = false;
      this.removeAttribute('open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.boundOnEsc);
    }

    async refresh() {
      try {
        const res = await fetch('/cart.js', CART_FETCH_OPTS);
        this.cart = await res.json();
        this.render();
        document.dispatchEvent(new CustomEvent('lali:cart:updated', { detail: this.cart }));
      } catch (err) { console.error('Cart fetch failed', err); }
    }

    async changeQty(line, delta) {
      const current = this.cart?.items?.[line - 1];
      if (!current) return;
      const newQty = Math.max(0, current.quantity + delta);
      await this.cartChange({ line, quantity: newQty });
    }

    async removeLine(line) {
      await this.cartChange({ line, quantity: 0 });
    }

    async addVariant(variantId) {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        });
        if (!res.ok) throw new Error(await res.text());
        if (this.pageMode) {
          window.location.reload();
        } else {
          await this.refresh();
        }
      } catch (err) { console.error('Upsell add failed', err); }
    }

    async cartChange(payload) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        this.cart = await res.json();
        document.dispatchEvent(new CustomEvent('lali:cart:updated', { detail: this.cart }));
        if (this.pageMode) {
          window.location.reload();
        } else {
          this.render();
        }
      } catch (err) { console.error('Cart change failed', err); }
    }

    render() {
      if (!this.cart) return;
      if (this.pageMode) {
        this.renderBanner();
        return;
      }
      const count = this.cart.item_count || 0;
      this.dataset.itemCount = count;
      this.renderBanner();

      if (count === 0) {
        if (this.itemsEl) this.itemsEl.innerHTML = '';
        this.emptyEl?.removeAttribute('hidden');
        this.footerEl?.setAttribute('hidden', '');
        this.upsellEl?.setAttribute('hidden', '');
      } else {
        this.emptyEl?.setAttribute('hidden', '');
        this.footerEl?.removeAttribute('hidden');
        this.upsellEl?.removeAttribute('hidden');
        this.renderItems();
        this.renderSubtotal();
      }
      this.updateHeaderBubble(count);
    }

    renderBanner() {
      if (!this.bannerEl) return;
      const subtotal = this.cart.total_price || 0;
      const threshold = this.threshold;
      if (threshold === 0 || subtotal >= threshold) {
        this.bannerEl.dataset.state = 'unlocked';
        this.bannerEl.textContent = this.bannerEl.dataset.textUnlocked || 'Obtuviste ENVÍO GRATIS 🎉';
      } else {
        const missing = threshold - subtotal;
        this.bannerEl.dataset.state = 'locked';
        const tmpl = this.bannerEl.dataset.textLocked || 'Te faltan [amount] para envío gratis';
        this.bannerEl.textContent = tmpl.replace(/\[amount\]|\{\{\s*amount\s*\}\}/g, money(missing, this.currencyFormat));
      }
    }

    renderItems() {
      if (!this.itemsEl) return;
      this.itemsEl.innerHTML = this.cart.items.map((item, i) => this.itemTemplate(item, i + 1)).join('');
    }

    itemTemplate(item, line) {
      const image = item.image ? `<img class="cart-drawer__item-img" src="${item.image.replace(/(\.[a-z]+)(\?.*)?$/, '_200x$1$2')}" alt="${item.product_title}" loading="lazy" />` : '';
      const priceCompare = item.original_price && item.original_price > item.final_price
        ? `<span class="cart-drawer__price-compare">${money(item.original_price, this.currencyFormat)}</span>`
        : '';
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<div class="cart-drawer__item-variant">${item.variant_title}</div>` : '';
      return `
        <li class="cart-drawer__item" data-line="${line}">
          <a class="cart-drawer__item-link" href="${item.url}">${image}</a>
          <div class="cart-drawer__item-info">
            <div class="cart-drawer__item-title">${item.product_title}</div>
            ${variantTitle}
            <div class="cart-drawer__item-prices">
              ${priceCompare}
              <span class="cart-drawer__price-final">${money(item.final_price, this.currencyFormat)}</span>
            </div>
            <div class="cart-drawer__item-controls">
              <div class="cart-drawer__qty">
                <button type="button" class="cart-drawer__qty-btn" data-cart-qty="-1" data-line="${line}" aria-label="Disminuir cantidad">−</button>
                <span class="cart-drawer__qty-val">${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn" data-cart-qty="1" data-line="${line}" aria-label="Aumentar cantidad">+</button>
              </div>
              <button type="button" class="cart-drawer__remove" data-cart-remove data-line="${line}" aria-label="Eliminar">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 7h16M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2m1 0v12a2 2 0 01-2 2H10a2 2 0 01-2-2V7"/></svg>
              </button>
            </div>
          </div>
        </li>`;
    }

    renderSubtotal() {
      if (this.subtotalEl) this.subtotalEl.textContent = money(this.cart.total_price || 0, this.currencyFormat);
      if (this.payBtn) {
        const baseLabel = this.payBtn.dataset.labelBase || 'PAGAR';
        this.payBtn.textContent = `${baseLabel} — ${money(this.cart.total_price || 0, this.currencyFormat)}`;
      }
    }

    updateHeaderBubble(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count;
        el.toggleAttribute('hidden', count === 0);
      });
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const badge = bubble.querySelector('.cart-count-bubble, [data-cart-count]');
        if (badge) {
          badge.textContent = count;
          badge.toggleAttribute('hidden', count === 0);
        }
      }
    }
  }

  customElements.define('lali-cart-drawer', LaliCartDrawer);
})();
